import {
  DependencyList,
  EffectCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {ObjectMapRef, ObjectState, ObjectToggleValues} from 'types/commons'

export function useDefaultState<S>(value: S, state?: ObjectState<S>) {
  const defaultState = useState(value)

  return state || defaultState
}

export function useDidMount(effect: EffectCallback) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, [])
}

export function useDidUpdate(effect: EffectCallback, deps?: DependencyList) {
  const initialize = useRef(false)

  useEffect(() => {
    if (initialize.current) {
      effect()
    } else {
      initialize.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export function useMapRef<T extends object>(data: T) {
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const defaultState = useRef(data)
  const refData = useRef({...data})

  const handleUpdateData = useCallback(() => setLastUpdate(new Date()), [])

  const handleSetData = useCallback(
    (values: Partial<T> | ((prev: T) => Partial<T>)) => {
      const map =
        typeof values === 'function' ? values(refData.current) : values

      for (const [key, value] of Object.entries(map)) {
        refData.current[key] = value
      }
    },
    [],
  )

  const handleReset = useCallback(() => {
    refData.current = {...defaultState.current}
    handleUpdateData()
  }, [handleUpdateData])

  return useMemo<ObjectMapRef<T>>(
    () => ({
      map: refData,
      setMapValue: handleSetData,
      updateMap: handleUpdateData,
      reset: handleReset,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastUpdate, handleUpdateData, handleSetData, handleReset],
  )
}

export function useToggle(initialState: boolean = false) {
  const [value, setValue] = useState(initialState)

  const handleToggle = useCallback(() => setValue((previous) => !previous), [])

  return useMemo<ObjectToggleValues>(
    () => ({value, setValue, toggle: handleToggle}),
    [handleToggle, value],
  )
}
